import React from 'react';

import { type InformationalMessage } from '@lib/entities/Store';

import useThemeColor from '@lib/hooks/useThemeColor';

import Stack from '@components/Stack';
import Typography from '@components/Typography';
import { Warning } from '@assets/svg';

interface InformationalMessageBannerProps {
    message?: InformationalMessage;
}

// Minimum height of the banner; it grows beyond this to fit a message that
// wraps to multiple lines.
const MIN_BANNER_HEIGHT = 72;

/**
 * Renders a server-driven informational message — Phase 0 of the
 * informational-messages framework. Today it surfaces temporary store closures,
 * but it is intentionally generic on `message.type` so future message types
 * (alerts, stockouts) can reuse it unchanged: it always renders the
 * server-rendered `title`/`body` verbatim and never hides on an unknown type.
 *
 * Renders nothing when there is no active message.
 */
const InformationalMessageBanner: React.FC<InformationalMessageBannerProps> = ({ message }) => {
    const white = useThemeColor('white');

    if (!message) {
        return null;
    }

    return (
        <Stack
            testID="informational-message-banner"
            backgroundColor="primary.dark"
            borderColor="primary.dark"
            border={2}
            row
            sx={{
                width: '100%',
                minHeight: MIN_BANNER_HEIGHT,
                padding: 12,
                gap: 12,
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Stack sx={{ flexShrink: 0, width: 26, height: 26 }}>
                <Warning color={white} width={26} height={26} />
            </Stack>
            <Stack sx={{ flexShrink: 1, gap: 6 }}>
                <Typography variant="p3bold" color="white">
                    {message.title}
                </Typography>
                {message.body ? (
                    <Typography variant="p3" color="white">
                        {message.body}
                    </Typography>
                ) : null}
            </Stack>
        </Stack>
    );
};

export default InformationalMessageBanner;
